import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import Component from '@glimmer/component'
import { themeKeys, themes } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { notEq } from 'ember-truth-helpers'
import type { SafeString } from '@ember/template'
import type { TOC } from '@ember/component/template-only'
import type { PhosphorIconName } from 'phosphor-icons'

export type StatBoxObject = {
  name?: string
  value: number | string | SafeString
}

interface Args {
  stats: StatBoxObject[][]
  theme?: keyof typeof themes
  title?: string | SafeString
  titleIcon?: PhosphorIconName
  tooltipText?: string
}

interface Signature {
  Element: Element
  Args: Args
}

/**
 * Pass this component a 2d array of rows of stats. The array dictates the structure of the stat-box.
 * This example, would produce stat box with StatObject1 on the top row, and 2 evenly split stats columns on the bottom row.
 * {
 *   title: 'my title',
 *   theme: 'some-theme',
 *   stats: [ [StatObject1], [StatObject2, StatObject3] ],
 * }
 *
 * A StatObject is an object comprising of an optional name and value keys
 * {
 *  name?: string
 *  value: number | string
 * }
 */
export class StatBox extends Component<Signature> {
  get colorTheme() {
    const theme = this.args.theme ?? themeKeys.THEME_DEFAULT
    return themes[theme]
  }

  <template>
    <stat-box data-test-stat-box class="print:border-dusty-black-200 flex flex-col print:border" ...attributes>
      <header
        data-test-stat-box-header
        class="header print:border-dusty-black-200 print:bg-dusty-black-50 relative flex h-14 shrink-0 grow-0 items-center justify-center text-center text-white print:m-1 print:border-b print:border-solid
          {{this.colorTheme.header}}"
      >
        {{! Extra horizontal padding necessary for tooltip icon }}
        <p class="px-6 text-base font-bold print:font-bold print:text-black">
          {{#if @titleIcon}}
            <PhosphorIcon @icon={{@titleIcon}} aria-hidden="true" class="mr-2" />
          {{/if}}
          {{@title}}
        </p>
        {{#if @tooltipText}}
          <span data-test-stat-box-tooltip class="absolute top-0 right-1 print:hidden">
            <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 h-3 w-3 cursor-pointer" />
            <Tooltip @text={{@tooltipText}} />
          </span>
        {{/if}}
      </header>

      <div
        data-test-stat-box-stats
        class="stats flex grow flex-col text-black print:bg-transparent {{this.colorTheme.stats}}"
      >
        {{#each @stats as |statsRow statsRowIndex|}}
          {{#if (notEq statsRowIndex 0)}}
            <div class="print:border-dusty-black-200 mx-2 border-t border-solid border-white"></div>
          {{/if}}
          <div
            class="print:border-dusty-black-200 flex shrink grow divide-x divide-solid divide-white print:divide-black"
            data-test-stat-box-stats-row={{statsRowIndex}}
          >
            {{#each statsRow as |statCol statColIndex|}}
              <StatItem @stat={{statCol}} @statIndex={{statColIndex}} />
            {{/each}}
          </div>
        {{/each}}
      </div>
    </stat-box>
  </template>
}

export default StatBox

interface StatItemSignature {
  Args: {
    stat: StatBoxObject
    statIndex: number
  }
}

const StatItem: TOC<StatItemSignature> = <template>
  <div class="stat my-2 w-full shrink grow p-2 text-center" data-test-stat-box-stat={{@statIndex}}>
    <p class="stat-value text-2xl leading-none" data-test-stat-box-stat-value>
      {{@stat.value}}
    </p>
    <p class="stat-name mt-1 text-base leading-none" data-test-stat-box-stat-name>
      {{@stat.name}}
    </p>
  </div>
</template>
